import { z } from 'zod';
import {
  WorkspaceId,
  ArtistId,
  TemplateId,
  GigId,
  LiveSessionId,
  ImprovProfileId,
  SetlistItemId,
  InteractionId,
} from '../ids.js';
import {
  Slug,
  PublicCode,
  IanaTimezone,
  IsoTimestamp,
  Timestamps,
  Version,
  Money,
} from '../primitives.js';
import { GigStatus, SetlistVisibilityMode } from '../enums.js';

/** Ordered public module keys the audience page may render (§15.1). */
export const PublicModule = z.enum([
  'artist_info',
  'now_playing',
  'setlist',
  'breaks',
  'song_requests',
  'create_a_song',
  'suggestions',
  'tips',
  'social_links',
  'custom_cta',
]);
export type PublicModule = z.infer<typeof PublicModule>;

export const PublicLayout = z.object({
  moduleOrder: z.array(PublicModule).default([]),
  primaryActions: z.array(PublicModule).max(3).default([]),
});
export type PublicLayout = z.infer<typeof PublicLayout>;

/** Tip preset amounts + copy (§22.4). */
export const TipPolicy = z.object({
  heading: z.string().max(80).optional(),
  thankYouText: z.string().max(280).optional(),
  presets: z.array(Money).max(8).default([]),
  allowCustom: z.boolean().default(true),
});
export type TipPolicy = z.infer<typeof TipPolicy>;

/** §27.4 GigTemplate — a versioned, reusable configuration bundle (§12). */
export const GigTemplate = z.object({
  templateId: TemplateId,
  workspaceId: WorkspaceId,
  artistId: ArtistId.nullable().default(null),
  name: z.string().min(1).max(120),
  templateVersion: Version.default(1),
  status: z.enum(['draft', 'active', 'archived']),
  featureOverrides: z.record(z.string(), z.unknown()).default({}),
  publicLayout: PublicLayout,
  interactionPolicy: z.record(z.string(), z.unknown()).default({}),
  setlistPolicy: z.object({ visibilityMode: SetlistVisibilityMode }).default({
    visibilityMode: 'current_and_next',
  }),
  tipPolicy: TipPolicy.default({ allowCustom: true, presets: [] }),
  notificationPolicy: z.record(z.string(), z.unknown()).default({}),
  improvProfileId: ImprovProfileId.nullable().default(null),
  themeOverrides: z.record(z.string(), z.unknown()).default({}),
  createdAt: IsoTimestamp,
  createdBy: z.string(),
  supersedesTemplateVersion: Version.nullable().default(null),
});
export type GigTemplate = z.infer<typeof GigTemplate>;

export const Venue = z.object({
  publicLabel: z.string().max(120).optional(),
  city: z.string().max(120).optional(),
});
export type Venue = z.infer<typeof Venue>;

/** §27.5 Gig. */
export const Gig = Timestamps.extend({
  gigId: GigId,
  workspaceId: WorkspaceId,
  artistId: ArtistId,
  templateId: TemplateId.nullable().default(null),
  templateVersion: Version.nullable().default(null),
  name: z.string().min(1).max(160),
  venue: Venue.default({}),
  timezone: IanaTimezone,
  scheduledStartAt: IsoTimestamp.nullable().default(null),
  scheduledEndAt: IsoTimestamp.nullable().default(null),
  publicCode: PublicCode,
  status: GigStatus,
  featureOverrides: z.record(z.string(), z.unknown()).default({}),
  publicLayout: PublicLayout,
  publishedVersion: Version.nullable().default(null),
  activeLiveSessionId: LiveSessionId.nullable().default(null),
  artistSlug: Slug,
  version: Version,
});
export type Gig = z.infer<typeof Gig>;

/** Break runtime state (§17.5). */
export const BreakState = z.object({
  active: z.boolean().default(false),
  heading: z.string().max(80).optional(),
  message: z.string().max(280).optional(),
  expectedReturnAt: IsoTimestamp.nullable().default(null),
  showCountdown: z.boolean().default(false),
  tipsAvailable: z.boolean().default(true),
  interactionsAvailable: z.boolean().default(true),
  venueMessage: z.string().max(280).optional(),
});
export type BreakState = z.infer<typeof BreakState>;

/** §27.6 LiveSession — runtime state of a gig. */
export const LiveSession = z.object({
  liveSessionId: LiveSessionId,
  workspaceId: WorkspaceId,
  artistId: ArtistId,
  gigId: GigId,
  status: z.enum(['live', 'intermission', 'ended']),
  startedAt: IsoTimestamp,
  endedAt: IsoTimestamp.nullable().default(null),
  currentSetlistItemId: SetlistItemId.nullable().default(null),
  currentInteractionId: InteractionId.nullable().default(null),
  breakState: BreakState.default({ active: false }),
  customNowPlaying: z.string().max(160).nullable().default(null),
  temporaryFeatureOverrides: z.record(z.string(), z.unknown()).default({}),
  snapshotVersion: Version.default(0),
  updatedAt: IsoTimestamp,
  version: Version,
});
export type LiveSession = z.infer<typeof LiveSession>;
